import { Component } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCw, Home } from 'lucide-react';

/**
 * AppErrorBoundary — the last line of defence against blank screens.
 *
 * Design goals (Phase 47):
 *   1. Do NOT scare users with a full-screen error card for isolated
 *      hiccups (e.g. a single bad data shape from a lagging API).
 *      → For the first ~2 crashes we auto-recover: show a small toast
 *        ("Kuch gadbad hui, retry karein"), remount the subtree and let
 *        the user keep working.
 *   2. Only show the full-screen fallback when we are in a real crash
 *      LOOP (3+ crashes in a 10 s window) — that means auto-retry is
 *      pointless and the user needs manual intervention.
 *   3. Still send a `/api/errors/client` beacon on every crash so the
 *      admin's System Errors page has a complete audit trail.
 */
const CRASH_WINDOW_MS = 10_000; // 10-second rolling window
const CRASH_LOOP_LIMIT = 3;      // >= 3 crashes in window → show fallback

export default class AppErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,          // controls the fallback UI
            error:    null,
            errorInfo:null,
            retryKey: 0,              // bump to remount children
        };
        this._crashTimestamps = [];   // rolling log of recent crash times
        this._autoRetryTimer = null;
    }

    componentWillUnmount() {
        if (this._autoRetryTimer) {
            clearTimeout(this._autoRetryTimer);
            this._autoRetryTimer = null;
        }
    }

    static getDerivedStateFromError(error) {
        // We intentionally do NOT set `hasError: true` here — that decision
        // is made in componentDidCatch after we check whether we can
        // auto-recover.  Returning null keeps the current state so React
        // still unmounts the crashed tree, and we schedule a remount below.
        return { error };
    }

    _reportBeacon(error, errorInfo) {
        try {
            const API = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
            if (!API || typeof fetch !== 'function') return;
            fetch(`${API}/api/errors/client`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(typeof localStorage !== 'undefined' && localStorage.getItem('surya_token')
                        ? { Authorization: `Bearer ${localStorage.getItem('surya_token')}` }
                        : {}),
                },
                body: JSON.stringify({
                    message: ((error && error.message) || String(error) || 'React render crash').slice(0, 800),
                    kind:    'ReactRenderCrash',
                    path:    (typeof window !== 'undefined' && window.location && window.location.pathname) || '',
                    stack:   ((error && error.stack) || '').slice(0, 5000),
                    extra:   {
                        component_stack: (errorInfo && errorInfo.componentStack ? errorInfo.componentStack.slice(0, 3000) : ''),
                        url:             (typeof window !== 'undefined' && window.location && window.location.href) || '',
                        ua:              (typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent.slice(0, 200) : ''),
                    },
                }),
                keepalive: true,
            }).catch(() => {});
        } catch { /* swallow — beacon must never throw */ }
    }

    componentDidCatch(error, errorInfo) {
        // Always log locally so we can inspect via chrome://inspect on the APK.
        // eslint-disable-next-line no-console
        console.error('[AppErrorBoundary] render crash:', error, errorInfo);
        this._reportBeacon(error, errorInfo);

        // Track crashes over a rolling window to detect true loops.
        const now = Date.now();
        this._crashTimestamps = this._crashTimestamps
            .filter(ts => now - ts < CRASH_WINDOW_MS);
        this._crashTimestamps.push(now);

        const looping = this._crashTimestamps.length >= CRASH_LOOP_LIMIT;
        if (looping) {
            // Genuine crash-loop → surface the fallback so the user can
            // manually Retry / Home / Reload.
            this.setState({ hasError: true, errorInfo });
            return;
        }

        // Otherwise: soft-recover.  Fire a tiny toast so the user knows
        // something hiccuped, then remount the subtree on the next tick.
        try {
            toast.error('कुछ गड़बड़ हुई, कृपया पुनः प्रयास करें', {
                description: 'Page refresh ho raha hai…',
                duration: 3500,
            });
        } catch { /* toast library not ready — ignore */ }

        if (this._autoRetryTimer) clearTimeout(this._autoRetryTimer);
        this._autoRetryTimer = setTimeout(() => {
            this.setState(prev => ({
                hasError:  false,
                error:     null,
                errorInfo: null,
                retryKey:  prev.retryKey + 1,
            }));
        }, 60);
    }

    handleRetry = () => {
        // Manual retry from the fallback UI — reset counters too so the
        // user gets a fresh window.
        this._crashTimestamps = [];
        this.setState(prev => ({
            hasError:  false,
            error:     null,
            errorInfo: null,
            retryKey:  prev.retryKey + 1,
        }));
    };

    handleGoHome = () => {
        try {
            this._crashTimestamps = [];
            if (typeof window !== 'undefined') {
                window.location.href = '/';
            }
        } catch { /* noop */ }
    };

    handleHardReload = () => {
        try {
            if (typeof window !== 'undefined' && window.location) {
                window.location.reload();
            }
        } catch { /* noop */ }
    };

    render() {
        if (this.state.hasError) {
            const err = this.state.error;
            const msg = (err && (err.message || String(err))) || 'Unknown error';
            return (
                <div
                    className='min-h-screen w-full grid place-items-center bg-background text-foreground p-6'
                    data-testid='app-error-boundary'
                >
                    <div className='max-w-md w-full rounded-2xl border border-destructive/25 bg-card shadow-lg p-6 text-center'>
                        <div className='mx-auto w-14 h-14 rounded-full bg-destructive/10 grid place-items-center'>
                            <AlertTriangle className='w-7 h-7 text-destructive' />
                        </div>
                        <h1 className='mt-3 text-xl font-bold'>कुछ गड़बड़ हो गयी</h1>
                        <p className='text-sm text-muted-foreground mt-1'>
                            App बार-बार crash ho raha hai. कृपया <b>Retry</b> ya <b>Home</b> दबाएँ — यदि फिर भी problem आये तो Admin को बताएँ।
                        </p>
                        <div className='mt-4 rounded-md bg-muted/60 border px-3 py-2 text-[11px] text-left break-all font-mono max-h-24 overflow-y-auto'>
                            {msg}
                        </div>
                        <div className='mt-5 grid grid-cols-2 gap-2'>
                            <Button
                                onClick={this.handleRetry}
                                className='w-full'
                                data-testid='error-boundary-retry'
                            >
                                <RotateCw className='w-4 h-4 mr-1.5' /> Retry
                            </Button>
                            <Button
                                variant='outline'
                                onClick={this.handleGoHome}
                                className='w-full'
                                data-testid='error-boundary-home'
                            >
                                <Home className='w-4 h-4 mr-1.5' /> Home
                            </Button>
                        </div>
                        <button
                            type='button'
                            onClick={this.handleHardReload}
                            className='mt-3 text-[11px] text-muted-foreground hover:text-foreground underline'
                            data-testid='error-boundary-hard-reload'
                        >
                            Full app reload
                        </button>
                    </div>
                </div>
            );
        }
        // Bump `key` on retryKey so a Retry (manual or auto) remounts the
        // whole subtree cleanly and clears any bad in-memory state.
        return <div key={this.state.retryKey} className='contents'>{this.props.children}</div>;
    }
}
